// @flow

import * as React from 'react'
import {
  Container,
  Grid,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Divider,
  withStyles,
  Box,
} from '@material-ui/core'
import { ThemeProvider } from '@material-ui/core/styles'

import theme from '../../../theme'
import { formatDate } from '../../../utils/dateFormatter'

import type { Claim, FormData } from '../Types'

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#595959',
    color: '#fff',
    fontSize: 14,
    textTransform: 'uppercase',
    fontWeight: 700,
  },
  root: {
    padding: 7,
    fontSize: 14,
  },
}))(TableCell)

const StyledTableRow = withStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.action.hover,
  },
}))(TableRow)

const StyledTitle = {
  color: '#A9A9A9',
  textTransform: 'uppercase',
  fontSize: 14,
  fontWeight: 700,
}

const StyledTotal = {
  ...StyledTitle,
  marginRight: 30,
}

function invoiceTotal(claims: Array<Claim>): string {
  let total = 0

  if (claims && claims.length !== 0) {
    total = claims.map((claim) => claim.privateBillingAmount).reduce((a, b) => a + b)
  }

  return total.toFixed(2)
}

function GenerateInvoice({
  user,
  invoiceDetails,
  patient,
  claims,
}: $ReadOnly<FormData>): React.Element<typeof ThemeProvider> {
  return (
    <ThemeProvider theme={theme}>
      <Box mt={4}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid item container direction="column" xs={8}>
              <Typography variant="h5">{user.name}</Typography>
              <Box mt={3} mb={3}>
                <Typography varient="body1">
                  {user.address1} <br />
                  {user.address2}
                  {user.address2.trim() !== '' && <br />}
                  {user.city}, {user.province} <br />
                  {user.postalCode}
                </Typography>
              </Box>
              <Box mb={3}>
                <Typography varient="body1">
                  {user.email} <br />
                  {user.phone}
                </Typography>
              </Box>
              <Box mt={3} mb={3}>
                <Typography variant="h6" style={StyledTitle}>
                  Billed To
                </Typography>
                <Typography varient="body1">
                  {patient.name} <br />
                  {patient.address1} <br />
                  {patient.address2}
                  {patient.address2.trim() !== '' && <br />}
                  {patient.city}, {patient.province} <br />
                  {patient.postalCode} <br />
                  {patient.country}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={4} container direction="column" alignItems="flex-end">
              <Typography variant="h4">Invoice</Typography>
              <Box mt={3} mb={2}>
                <Typography variant="h6" style={StyledTitle}>
                  Invoice Number
                </Typography>
                <Typography varient="body1" align="right">
                  {invoiceDetails.number}
                </Typography>
              </Box>
              <Box mb={3}>
                <Typography variant="h6" style={StyledTitle}>
                  Date of issue
                </Typography>
                <Typography varient="body1" align="right">
                  {invoiceDetails.date && formatDate(invoiceDetails.date)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <TableContainer>
            <Table aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell width="20%">Date</StyledTableCell>
                  <StyledTableCell width="60%">Description</StyledTableCell>
                  <StyledTableCell width="20%" align="right">
                    Amount
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims &&
                  claims.map((claim, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{formatDate(claim.dateOfService)}</StyledTableCell>
                      <StyledTableCell>
                        {claim.code} - {claim.description}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        ${claim.privateBillingAmount.toFixed(2)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2}>
            <Typography variant="body1" align="right">
              <span style={StyledTotal}>Invoice Total:</span>
              <span style={{ marginRight: '7px' }}> ${invoiceTotal(claims)}</span>
            </Typography>
          </Box>
          <Box mt={3} mb={3}>
            <Typography variant="h6" style={StyledTitle}>
              Terms
            </Typography>
            <Typography variant="body1">{invoiceDetails.terms}</Typography>
          </Box>

          <div style={{ pageBreakInside: 'avoid' }}>
            <Divider />
            <Box mt={2}>
              <Box mt={3} mb={3}>
                <Typography variant="body1">
                  How to pay (include this portion with your payment)
                </Typography>
              </Box>
              <Box mt={3} mb={3}>
                <Typography varient="h5">
                  <b>Cheque</b>
                </Typography>
                <Grid container spacing={3}>
                  <Grid item lg={2} xs={3}>
                    <Typography varient="subtitle1">
                      Make payable to: <br />
                      Mailing Address: <br />
                      Invoice #: <br />
                      Amount:
                    </Typography>
                  </Grid>
                  <Grid item lg={8} xs={8}>
                    <Typography varient="subtitle1">
                      {user.name} <br />
                      {user.address1}, {user.address2 ? `${user.address2},` : ''} {user.city},{' '}
                      {user.province}, {user.postalCode} <br />
                      {invoiceDetails.number} <br />${invoiceTotal(claims)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </div>
        </Container>
      </Box>
    </ThemeProvider>
  )
}

export default GenerateInvoice
